import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { CredentialsService } from '@app/core';
import { AuthenticationService, NotyService, OAuthAuthorizationConsentResponse, OAuthAuthorizationService } from '@app/services';

@Component({
  selector: 'app-oauth-authorize',
  templateUrl: './oauth-authorize.component.html',
  styleUrls: ['./oauth-authorize.component.scss'],
  standalone: false
})
export class OAuthAuthorizeComponent implements OnInit {
  consent: OAuthAuthorizationConsentResponse | null = null;
  selectedScopes: Record<string, boolean> = {};
  isLoading = true;
  isSubmitting = false;
  private loginRedirected = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private credentialsService: CredentialsService,
    private authenticationService: AuthenticationService,
    private oauthAuthorizationService: OAuthAuthorizationService,
    private notyService: NotyService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    const browserAuthorizeError = this.oauthErrorMessage(this.route.snapshot.queryParams);

    if (browserAuthorizeError) {
      this.notyService.error(this.translateService.instant(browserAuthorizeError), 20000);
      this.redirectToLogin();
      return;
    }

    if (this.route.snapshot.queryParams.prompt === 'login') {
      this.forceLogin();
      return;
    }

    if (!this.credentialsService.isAuthenticated()) {
      this.redirectToLogin(this.router.url);
      return;
    }

    this.createAuthorizationRequest();
  }

  approve() {
    if (!this.consent || this.isSubmitting) {
      return;
    }

    const scopes = this.consent.scopes.filter(scope => this.selectedScopes[scope.id]).map(scope => scope.id);

    if (scopes.length === 0) {
      this.deny();
      return;
    }

    this.isSubmitting = true;

    this.oauthAuthorizationService
      .approve(this.consent.auth_token, scopes)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: response => this.redirectToClient(response.redirect_url),
        error: error => this.handleError(error)
      });
  }

  deny() {
    if (!this.consent || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.oauthAuthorizationService
      .deny(this.consent.auth_token)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: response => this.redirectToClient(response.redirect_url),
        error: error => this.handleError(error)
      });
  }

  toggleScope(scopeId: string) {
    if (this.isSubmitting) {
      return;
    }

    this.selectedScopes[scopeId] = !this.selectedScopes[scopeId];
  }

  userName(): string {
    return this.consent?.user.name || this.consent?.user.email || this.consent?.user.phone || '';
  }

  userContact(): string {
    if (!this.consent?.user.name) {
      return '';
    }

    return this.consent.user.email || this.consent.user.phone || '';
  }

  isClientVerified(): boolean {
    const client = this.consent?.client;
    return !!client?.description?.trim() && !!client.icon_url?.trim();
  }

  scopeDescription(scope: { id: string; description: string }): string {
    const normalizedScope = (scope.id || '').trim().toLowerCase();
    const translationKey = `settings.oauthApplications.scopePermissions.${normalizedScope}`;
    const translation = this.translateService.instant(translationKey);

    return translation !== translationKey ? translation : scope.description || normalizedScope;
  }

  createAuthorizationRequest() {
    this.isLoading = true;

    this.oauthAuthorizationService
      .create(this.oauthParameters())
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: response => {
          if (response.status === 'redirect') {
            this.redirectToClient(response.redirect_url);
            return;
          }

          this.consent = response;
          this.selectedScopes = response.scopes.reduce((selected, scope) => ({ ...selected, [scope.id]: true }), {});
        },
        error: error => this.handleError(error)
      });
  }

  private forceLogin() {
    const redirect = this.oauthUrlWithoutPromptLogin();

    this.authenticationService
      .logout()
      .pipe(finalize(() => this.redirectToLogin(redirect)))
      .subscribe();
  }

  private redirectToLogin(redirect?: string) {
    if (this.loginRedirected) {
      return;
    }

    this.loginRedirected = true;
    this.router.navigate(['/entrar'], {
      replaceUrl: true,
      queryParams: redirect ? { redirect } : {}
    });
  }

  private redirectToClient(redirectUrl: string) {
    window.location.assign(redirectUrl);
  }

  private oauthUrlWithoutPromptLogin(): string {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams.prompt;

    return this.router.serializeUrl(this.router.createUrlTree(['/oauth/authorize'], { queryParams }));
  }

  private oauthParameters(): Record<string, string> {
    return Object.entries(this.route.snapshot.queryParams).reduce(
      (parameters, [key, value]) => {
        if (Array.isArray(value)) {
          parameters[key] = value[0];
        } else if (value !== null && typeof value !== 'undefined') {
          parameters[key] = String(value);
        }

        return parameters;
      },
      {} as Record<string, string>
    );
  }

  private handleError(error: any) {
    const errorMessage = this.oauthErrorMessage(error?.error) || this.oauthErrorMessage(error?.errors?.[0]) || 'oauth.error';

    this.notyService.error(this.translateService.instant(errorMessage), 20000);
    this.redirectToLogin();
  }

  private oauthErrorMessage(error: any): string | null {
    const errorKey = error?.error || error?.message;

    if (typeof errorKey !== 'string' || !/^[a-z][a-z_]*$/.test(errorKey)) {
      return null;
    }

    return `oauth.errors.${errorKey}`;
  }
}
